import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { BookOpenIcon, CheckIcon, LockIcon, RotateCcwIcon, StarIcon } from "lucide-react";
import { toast } from "sonner";
import type { LessonNode, LessonStatus } from "../../types/learning";
import { spring } from "../../utils/motion";
interface LessonNodeButtonProps {
  lesson: LessonNode;
  status: LessonStatus;
  href: string;
  offset: number;
}
const kindIcon = {
  lesson: StarIcon,
  story: BookOpenIcon,
  review: RotateCcwIcon
};
export function LessonNodeButton({
  lesson,
  status,
  href,
  offset
}: LessonNodeButtonProps) {
  const navigate = useNavigate();
  const reduce = useReducedMotion() ?? false;
  const Icon = status === "done" ? CheckIcon : status === "locked" ? LockIcon : kindIcon[lesson.kind];
  const colors = status === "done" ? "bg-mint text-good" : status === "current" ? "bg-accent text-night" : "bg-sunken text-ink-2";
  const onClick = () => {
    if (status === "locked") {
      toast("Locked for now", {
        description: "Finish the lessons before this one to unlock it."
      });
      return;
    }
    navigate(href);
  };
  return <li className="relative flex flex-col items-center" style={{
    transform: `translateX(${offset}px)`
  }} data-id="element-122" data-mp="2q42qveg0evoh">
      {status === "current" && <motion.span initial={reduce ? false : {
      opacity: 0,
      y: 8,
      scale: 0.96
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      ...spring.bouncy,
      delay: 0.3
    }} className="relative mb-3 rounded-xl border-3 border-line bg-surface px-3 py-1.5 text-sm font-bold text-ink shadow-brutal-sm" data-id="element-123" data-mp="2l5j1f427j1rd">
          Start
          <span className="absolute -bottom-[8px] left-1/2 h-3.5 w-3.5 -translate-x-1/2 rotate-45 border-b-3 border-r-3 border-line bg-surface" data-id="element-124" data-mp="22dykx0fcmjub" />
        </motion.span>}
      <motion.button type="button" onClick={onClick} aria-label={`${lesson.title} — ${status}`} whileHover={status === "locked" || reduce ? undefined : {
      y: -3
    }} whileTap={{
      scale: 0.94
    }} transition={spring.snappy} className={`focus-ring relative grid place-items-center rounded-full border-3 border-line shadow-brutal ${colors} ${status === "current" ? "h-[84px] w-[84px]" : "h-[72px] w-[72px]"}`} data-id="element-125" data-mp="19puf8ews26rm">
        <Icon className={status === "current" ? "h-8 w-8" : "h-7 w-7"} strokeWidth={2.5} aria-hidden="true" data-id="element-126" data-mp="3nl1tyfi1pdqs" />
      </motion.button>
      <span className={`mt-2 max-w-[120px] text-center text-sm font-semibold ${status === "locked" ? "text-ink-2" : "text-ink"}`} data-id="element-127" data-mp="2vr9f9fh0o09s">
        {lesson.title}
      </span>
    </li>;
}